import React, { useEffect, useState } from 'react';
import { documentsService, Document } from '../services/documents.service';
import { propertiesService, Property } from '../services/properties.service';
import { categoriesService, Category } from '../services/categories.service';
import { expensesService, CreateExpenseData } from '../services/expenses.service';
import toast from 'react-hot-toast';
import { FiFile, FiDownload, FiTrash2, FiEye, FiCalendar, FiFilter, FiRefreshCw, FiMail, FiDollarSign, FiHome, FiZap, FiCheckSquare, FiSquare, FiX, FiCode, FiFileText, FiSave, FiSkipForward, FiCreditCard } from 'react-icons/fi';
import { api } from '../config/api';
import DOMPurify from 'dompurify';
import { tw, monoFont, expenseCategoryColors, getCategoryStyle } from '../utils/terminalTheme';

export const Documents: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null);
  const [showSubscriptionsOnly, setShowSubscriptionsOnly] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'text' | 'html'>('text');
  const [categories, setCategories] = useState<Category[]>([]);
  const [showQuickAssign, setShowQuickAssign] = useState(false);
  const [quickAssignData, setQuickAssignData] = useState<CreateExpenseData>({
    property_id: undefined,
    category_id: undefined,
    document_id: undefined,
    amount: 0,
    expense_date: new Date().toISOString().split('T')[0],
    vendor: '',
    description: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string>('');  // For PDF/image preview
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [extractingSubscription, setExtractingSubscription] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState({
    site_name: '',
    monthly_price: 0,
    billing_cycle: 'Monthly',
    description: '',
    url: '',
    category: ''
  });
  const [existingSubscription, setExistingSubscription] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, [selectedPropertyId, showSubscriptionsOnly]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (documentUrl) {
        URL.revokeObjectURL(documentUrl);
      }
    };
  }, []);

  useEffect(() => {
    // Select first document when documents load
    if (documents.length > 0 && !selectedDocument) {
      // Create a mock event for initial load
      const mockEvent = { ctrlKey: false, metaKey: false, shiftKey: false } as React.MouseEvent;
      handleDocumentClick(documents[0], 0, mockEvent);
    }
  }, [documents]);

  useEffect(() => {
    // Keyboard navigation
    const handleKeyDown = (e: KeyboardEvent) => {
      if (documents.length === 0) return;

      // Don't handle shortcuts if user is typing in an input
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        // Only allow Enter key in inputs for quick save
        if (e.key === 'Enter' && showQuickAssign && !e.shiftKey) {
          e.preventDefault();
          handleQuickSaveExpense();
        }
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const newIndex = Math.min(selectedIndex + 1, documents.length - 1);
        const mockEvent = { ctrlKey: false, metaKey: false, shiftKey: false } as React.MouseEvent;
        handleDocumentClick(documents[newIndex], newIndex, mockEvent);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const newIndex = Math.max(selectedIndex - 1, 0);
        const mockEvent = { ctrlKey: false, metaKey: false, shiftKey: false } as React.MouseEvent;
        handleDocumentClick(documents[newIndex], newIndex, mockEvent);
      } else if (e.key === 'Delete' && selectedDocument) {
        e.preventDefault();
        handleDelete(selectedDocument.id);
      } else if (e.key === 'Enter' && showQuickAssign) {
        e.preventDefault();
        handleQuickSaveExpense();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, documents, selectedDocument, showQuickAssign]);

  const loadData = async (showRefreshing = false) => {
    if (showRefreshing) setIsRefreshing(true);
    try {
      const [docsData, propsData, catsData] = await Promise.all([
        documentsService.getDocuments({
          property_id: selectedPropertyId || undefined,
          include_all: true
        }),
        propertiesService.getProperties(),
        categoriesService.getExpenseCategories()
      ]);

      // Filter for subscriptions if the flag is set
      const filteredDocs = showSubscriptionsOnly
        ? docsData.filter((doc: any) => doc.document_type === 'SUBSCRIPTION')
        : docsData;

      setDocuments(filteredDocs);
      setProperties(propsData);
      setCategories(catsData);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadData(true);
  };

  const loadDocumentContent = async (document: Document) => {
    // Load email content if needed
    if ((document.from_email || document.email_subject) && !document.ocr_text && document.file_path) {
      try {
        const response = await api.get(`/documents/${document.id}/content`);
        document.ocr_text = response.data.content || response.data.text || '';
        // Update the document in the list
        setDocuments(prev => prev.map(d => d.id === document.id ? {...d, ocr_text: document.ocr_text} : d));
      } catch (error) {
        console.error('Failed to load email content:', error);
      }
    }

    // Load PDF/Image preview
    // Clean up previous URL
    if (documentUrl) {
      URL.revokeObjectURL(documentUrl);
      setDocumentUrl('');
    }

    // Skip if email content only or not a PDF/image
    if (document.file_path === 'email_content' || !document.filename?.match(/\.(pdf|jpg|jpeg|png|gif|bmp|webp)$/i)) {
      return;
    }

    setLoadingPreview(true);
    try {
      const response = await api.get(`/documents/${document.id}/view`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: response.data.type || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setDocumentUrl(url);
    } catch (error) {
      console.error('Failed to load document preview:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const prepareQuickAssignData = (document: Document) => {
    // Prepare expense data from AI extraction or defaults
    const aiData = document.extracted_data;

    // Map AI expense type to category
    let categoryId: number | undefined = undefined;
    if (aiData?.ai_expense_type && categories.length > 0) {
      const typeMapping: { [key: string]: string } = {
        'utilities': 'Utilities',
        'maintenance': 'Professional Services',
        'insurance': 'Auto Insurance',
        'supplies': 'Office Supplies',
        'hoa': 'Professional Services',
        'property_management': 'Professional Services',
        'repairs': 'Professional Services',
        'cleaning': 'Professional Services',
        'landscaping': 'Professional Services',
        'legal': 'Legal',
        'accounting': 'Accounting',
        'marketing': 'Marketing & Advertising',
        'travel': 'Travel & Entertainment',
        'meals': 'Meals & Entertainment',
        'software': 'Software & Subscriptions',
        'office': 'Office Expenses',
        'vehicle': 'Vehicle Expenses',
        'gas': 'Gas & Fuel',
        'fuel': 'Gas & Fuel'
      };

      const mappedCategoryName = typeMapping[aiData.ai_expense_type.toLowerCase()];
      if (mappedCategoryName) {
        const category = categories.find(c => c.name === mappedCategoryName);
        if (category) {
          categoryId = category.id;
        }
      }
    }

    setQuickAssignData({
      property_id: document.property_id || undefined,
      category_id: categoryId,
      document_id: document.id,
      amount: aiData?.ai_amount || 0,
      expense_date: aiData?.ai_service_date || new Date().toISOString().split('T')[0],
      vendor: aiData?.ai_vendor || '',
      description: aiData?.ai_description || ''
    });

    setShowQuickAssign(true);
  };

  const handleDocumentClick = async (document: Document, index: number, event: React.MouseEvent) => {
    // Check if Ctrl (Windows/Linux) or Cmd (Mac) is held
    if (event.ctrlKey || event.metaKey) {
      // Multi-select mode with Ctrl/Cmd+Click
      const newSelectedIds = new Set(selectedIds);
      if (newSelectedIds.has(document.id)) {
        newSelectedIds.delete(document.id);
      } else {
        newSelectedIds.add(document.id);
      }
      setSelectedIds(newSelectedIds);

      // Also select the document for viewing
      setSelectedDocument(document);
      setSelectedIndex(index);
      setViewMode('text'); // Reset to text view when switching documents
      await loadDocumentContent(document);
      prepareQuickAssignData(document);
    } else if (event.shiftKey && selectedIndex !== -1 && documents.length > 0) {
      // Shift+Click for range selection
      const start = Math.min(selectedIndex, index);
      const end = Math.max(selectedIndex, index);
      const newSelectedIds = new Set(selectedIds);

      for (let i = start; i <= end; i++) {
        newSelectedIds.add(documents[i].id);
      }
      setSelectedIds(newSelectedIds);

      // View the clicked document
      setSelectedDocument(document);
      setSelectedIndex(index);
      setViewMode('text'); // Reset to text view when switching documents
      await loadDocumentContent(document);
    } else {
      // Normal click - just view the document and clear selection
      setSelectedDocument(document);
      setSelectedIndex(index);
      setViewMode('text'); // Reset to text view when switching documents
      await loadDocumentContent(document);

      // Clear selection on normal click
      if (selectedIds.size > 0 && !selectedIds.has(document.id)) {
        setSelectedIds(new Set());
      }
    }
  };

  const selectAll = () => {
    const allIds = new Set(documents.map(d => d.id));
    setSelectedIds(allIds);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) {
      toast.error('No documents selected');
      return;
    }

    const confirmMessage = selectedIds.size === 1
      ? 'Are you sure you want to delete this document?'
      : `Are you sure you want to delete ${selectedIds.size} documents?`;

    if (!window.confirm(confirmMessage)) return;

    let successCount = 0;
    let failCount = 0;

    // Show loading toast
    const loadingToast = toast.loading(`Deleting ${selectedIds.size} documents...`);

    const idsToDelete = Array.from(selectedIds);
    for (const id of idsToDelete) {
      try {
        await documentsService.deleteDocument(id);
        successCount++;
        // Remove from documents list immediately
        setDocuments(prev => prev.filter(d => d.id !== id));
      } catch (error: any) {
        console.error(`Failed to delete document ${id}:`, error);
        failCount++;
      }
    }

    // Dismiss loading toast
    toast.dismiss(loadingToast);

    // Show result
    if (failCount === 0) {
      toast.success(`Successfully deleted ${successCount} document${successCount > 1 ? 's' : ''}`);
    } else {
      toast.error(`Deleted ${successCount} document${successCount > 1 ? 's' : ''}, ${failCount} failed`);
    }

    // Clear selection
    setSelectedIds(new Set());

    // Clear document preview if it was deleted
    if (selectedDocument && selectedIds.has(selectedDocument.id)) {
      setSelectedDocument(null);
    }

    // Reload data to ensure sync
    loadData();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await documentsService.deleteDocument(id);
      toast.success('Document deleted successfully');
      // Remove the document from the list immediately for better UX
      setDocuments(prev => prev.filter(d => d.id !== id));
      // Clear selection if deleted document was selected
      if (selectedDocument?.id === id) {
        setSelectedDocument(null);
      }
      // Reload data to ensure sync with server
      loadData();
    } catch (error: any) {
      console.error('Delete error:', error);
      if (error.response?.status === 404) {
        toast.error('Document not found - refreshing list');
        loadData();
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to delete document');
      }
    }
  };

  const handleDownload = async (document: Document) => {
    try {
      const response = await api.get(`/documents/${document.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleAssignProperty = async (documentId: string, propertyId: number) => {
    try {
      await documentsService.updateDocument(documentId, { property_id: propertyId });
      toast.success('Property assigned successfully');
      loadData(true);
    } catch (error) {
      toast.error('Failed to assign property');
    }
  };

  const handleQuickSaveExpense = async () => {
    if (!quickAssignData.amount || quickAssignData.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsSaving(true);
    try {
      await expensesService.createExpense(quickAssignData);
      toast.success('Expense created successfully!');

      // Mark document as processed (remove from list)
      setDocuments(prev => prev.filter(d => d.id !== selectedDocument?.id));

      // Auto-advance to next document
      const nextIndex = Math.min(selectedIndex, documents.length - 2);
      if (documents.length > 1 && nextIndex >= 0) {
        const nextDoc = documents.find((_, idx) => idx === (selectedIndex === nextIndex ? nextIndex + 1 : nextIndex));
        if (nextDoc) {
          const mockEvent = { ctrlKey: false, metaKey: false, shiftKey: false } as React.MouseEvent;
          handleDocumentClick(nextDoc, nextIndex, mockEvent);
        }
      } else {
        setSelectedDocument(null);
        setShowQuickAssign(false);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create expense');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkAsSubscription = async () => {
    if (!selectedDocument) return;

    setExtractingSubscription(true);
    try {
      const response = await api.post(`/subscriptions/extract-from-document/${selectedDocument.id}`);
      const { existing_subscription, ...extractedData } = response.data;
      setSubscriptionData(extractedData);
      setExistingSubscription(existing_subscription);
      setShowSubscriptionModal(true);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to extract subscription data');
    } finally {
      setExtractingSubscription(false);
    }
  };

  const handleSaveSubscription = async () => {
    if (!selectedDocument) return;

    setIsSaving(true);
    try {
      await api.post(`/subscriptions/from-document/${selectedDocument.id}`, subscriptionData);
      toast.success('Subscription saved successfully!');
      setShowSubscriptionModal(false);
      setExistingSubscription(null);
      loadData(); // Refresh to show updated document type
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save subscription');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLinkToExisting = async () => {
    if (!selectedDocument || !existingSubscription) return;

    setIsSaving(true);
    try {
      await api.post(`/subscriptions/${existingSubscription.id}/link-document/${selectedDocument.id}`);
      toast.success('Document linked to existing subscription!');
      setShowSubscriptionModal(false);
      setExistingSubscription(null);
      loadData(); // Refresh to show updated document type
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to link document');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPropertyName = (propertyId: number) => {
    const property = properties.find(p => p.id === propertyId);
    return property ? property.name : 'Unknown Property';
  };

  if (isLoading) {
    return (
      <div className={tw.loadingContainer}>
        <div className={tw.loading}></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-900">
      {/* Terminal Header */}
      <div className="p-4 border-b border-gray-700/50 bg-gray-800/80">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-mono text-white" style={monoFont}>
              <span className="text-cyan-400">~/</span>documents
              <span className="text-gray-600 text-sm font-normal ml-3">
                <span className="text-green-500">$</span> ls --inbox
              </span>
            </h1>

            {/* Enhanced Help Section */}
            <div className="mt-2 mb-3 space-y-2">
              <div className="p-3 bg-gray-800/80 rounded-lg border border-cyan-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <FiMail className="w-4 h-4 text-cyan-400" />
                  <span className="font-mono font-medium text-cyan-400 text-sm">How Document Processing Works</span>
                </div>
                <div className="text-gray-400 text-xs font-mono leading-relaxed space-y-1">
                  <p><strong className="text-gray-300">Property Documents:</strong> Send to property-specific addresses (e.g., 3540@gpume.com for 3540 Cactus Shadow St)</p>
                  <p><strong className="text-gray-300">Subscription Bills:</strong> Send to subscribe@gpume.com - automatically tagged as subscriptions (red badge), never assigned to properties</p>
                  <p><strong className="text-gray-300">AI Processing:</strong> Automatically extracts vendor, amount, dates, and classifies as Invoice/Receipt/HOA/Other</p>
                  <p><strong className="text-gray-300">Quick Expense:</strong> Create expenses with one click from AI suggestions</p>
                  <p><strong className="text-gray-300">Inline Viewing:</strong> PDFs and images display directly - no downloads needed</p>
                </div>
              </div>

              <div className="p-2 bg-gray-900/50 rounded border border-gray-700/50">
                <p className="text-gray-500 text-xs font-mono">
                  <strong className="text-gray-400">Keyboard Shortcuts:</strong> Up/Down Navigate | Delete key to remove | Enter for quick expense | Ctrl+Click to multi-select
                </p>
              </div>
            </div>
            <p className="text-gray-500 text-sm font-mono">
              {selectedIds.size > 0
                ? `${selectedIds.size} document${selectedIds.size !== 1 ? 's' : ''} selected | Ctrl+Click to select`
                : 'Ctrl+Click to multi-select | Shift+Click for range | Up/Down arrow keys to navigate'}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {selectedIds.size > 0 ? (
              <>
                <button
                  onClick={selectAll}
                  className={tw.btnSecondary}
                >
                  Select All ({documents.length})
                </button>
                <button
                  onClick={clearSelection}
                  className={tw.btnSecondary}
                >
                  Clear Selection
                </button>
                <button
                  onClick={handleDeleteSelected}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-mono text-sm transition-colors"
                >
                  <FiTrash2 className="w-4 h-4" />
                  <span>Delete {selectedIds.size} Selected</span>
                </button>
              </>
            ) : (
              <>
                <select
                  value={showSubscriptionsOnly ? 'subscriptions' : (selectedPropertyId || '')}
                  onChange={(e) => {
                    if (e.target.value === 'subscriptions') {
                      setShowSubscriptionsOnly(true);
                      setSelectedPropertyId(null);
                    } else {
                      setShowSubscriptionsOnly(false);
                      setSelectedPropertyId(e.target.value ? Number(e.target.value) : null);
                    }
                  }}
                  className={tw.select}
                  style={{ width: 'auto' }}
                >
                  <option value="">All Properties</option>
                  <option value="subscriptions">Subscriptions</option>
                  {properties.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className={tw.btnPrimary}
                >
                  <FiRefreshCw className={`w-4 h-4 inline mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Split View */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Document List */}
        <div className="w-2/5 border-r border-gray-700/50 overflow-y-auto bg-gray-900/50">
          {documents.length === 0 ? (
            <div className={tw.empty}>
              <FiFile className="w-12 h-12 text-gray-700 mx-auto mb-4" />
              <p>No documents found</p>
            </div>
          ) : (
            documents.map((doc, index) => (
              <div
                key={doc.id}
                onClick={(e) => handleDocumentClick(doc, index, e)}
                className={`p-3 border-b border-gray-700/30 cursor-pointer transition-colors relative font-mono ${
                  selectedIds.has(doc.id)
                    ? 'bg-cyan-900/20 border-l-4 border-l-cyan-500'
                    : selectedIndex === index
                    ? 'bg-cyan-900/30'
                    : 'hover:bg-gray-700/20'
                }`}
              >
                <div className="flex items-start space-x-3">
                  {doc.from_email ? (
                    <FiMail className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                  ) : (
                    <FiFile className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                  )}
                  {selectedIds.has(doc.id) && (
                    <div className="absolute left-2 top-2">
                      <FiCheckSquare className="w-4 h-4 text-cyan-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white font-mono truncate">
                      {doc.email_subject || doc.filename}
                    </p>
                    <p className="text-xs text-gray-500 font-mono truncate">
                      {doc.from_email || 'Document'}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-xs text-gray-600 font-mono">
                        {formatDate(doc.created_at)}
                      </span>
                      {doc.property_id && (
                        <span className="text-xs px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded font-mono">
                          {getPropertyName(doc.property_id)}
                        </span>
                      )}
                      {doc.document_type === 'SUBSCRIPTION' && (
                        <span className="text-xs px-1.5 py-0.5 bg-red-500/15 text-red-400 border border-red-500/30 rounded font-mono">
                          Subscription
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Right Panel - Document Preview */}
        <div className="flex-1 overflow-y-auto bg-gray-900">
          {selectedDocument ? (
            <div className="p-6">
              {/* Document Header */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold font-mono text-white">
                    {selectedDocument.email_subject || selectedDocument.filename}
                  </h2>
                  <div className="flex items-center space-x-2">
                    {!selectedDocument.subscription_id && (
                      <button
                        onClick={handleMarkAsSubscription}
                        disabled={extractingSubscription}
                        className="p-2 text-purple-400 hover:text-purple-300 disabled:opacity-50 transition-colors"
                        title="Mark as Subscription"
                      >
                        <FiCreditCard className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDownload(selectedDocument)}
                      className="p-2 text-gray-500 hover:text-cyan-400 transition-colors"
                      title="Download"
                    >
                      <FiDownload className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(selectedDocument.id)}
                      className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                      title="Delete"
                    >
                      <FiTrash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Email Headers */}
                {selectedDocument.from_email && (
                  <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-4 space-y-2">
                    <div className="flex">
                      <span className="text-gray-500 w-20 text-sm font-mono">From:</span>
                      <span className="text-white text-sm font-mono">{selectedDocument.from_email}</span>
                    </div>
                    <div className="flex">
                      <span className="text-gray-500 w-20 text-sm font-mono">Date:</span>
                      <span className="text-white text-sm font-mono">
                        {selectedDocument.email_received_at ?
                          new Date(selectedDocument.email_received_at).toLocaleString() :
                          'Unknown'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Property Assignment */}
                {!selectedDocument.property_id && selectedDocument.document_type !== 'SUBSCRIPTION' && (
                  <div className="mt-4 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <FiHome className="w-4 h-4 text-amber-400" />
                      <span className="text-amber-400 text-sm font-mono font-semibold">Assign to Property</span>
                    </div>
                    <select
                      onChange={(e) => e.target.value && handleAssignProperty(selectedDocument.id, Number(e.target.value))}
                      className={tw.select}
                    >
                      <option value="">Select a property...</option>
                      {properties.map((property) => (
                        <option key={property.id} value={property.id}>
                          {property.name} - {property.address}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Quick Assign Expense Form */}
                {showQuickAssign && (
                  <div className="mt-4 bg-gray-800/80 border border-emerald-500/30 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-emerald-400 font-mono font-semibold flex items-center text-sm">
                        <FiDollarSign className="w-4 h-4 mr-2" />
                        Quick Assign Expense
                      </h4>
                      <button
                        onClick={() => setShowQuickAssign(false)}
                        className="text-gray-500 hover:text-white transition-colors"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {/* Property */}
                      <div>
                        <label className={tw.label}>Property</label>
                        <select
                          value={quickAssignData.property_id || ''}
                          onChange={(e) => setQuickAssignData({...quickAssignData, property_id: e.target.value ? Number(e.target.value) : undefined})}
                          className={tw.select}
                        >
                          <option value="">Select Property</option>
                          {properties.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Category */}
                      <div>
                        <label className={tw.label}>Category</label>
                        <select
                          value={quickAssignData.category_id || ''}
                          onChange={(e) => setQuickAssignData({...quickAssignData, category_id: e.target.value ? Number(e.target.value) : undefined})}
                          className={tw.select}
                        >
                          <option value="">Select Category</option>
                          {categories.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Amount */}
                      <div>
                        <label className={tw.label}>Amount *</label>
                        <input
                          type="number"
                          step="0.01"
                          value={quickAssignData.amount}
                          onChange={(e) => setQuickAssignData({...quickAssignData, amount: parseFloat(e.target.value) || 0})}
                          className={tw.input}
                          placeholder="0.00"
                        />
                      </div>

                      {/* Date */}
                      <div>
                        <label className={tw.label}>Date</label>
                        <input
                          type="date"
                          value={quickAssignData.expense_date}
                          onChange={(e) => setQuickAssignData({...quickAssignData, expense_date: e.target.value})}
                          className={tw.input}
                        />
                      </div>

                      {/* Vendor */}
                      <div className="col-span-2">
                        <label className={tw.label}>Vendor</label>
                        <input
                          type="text"
                          value={quickAssignData.vendor || ''}
                          onChange={(e) => setQuickAssignData({...quickAssignData, vendor: e.target.value})}
                          className={tw.input}
                          placeholder="Vendor name"
                        />
                      </div>

                      {/* Description */}
                      <div className="col-span-2">
                        <label className={tw.label}>Description</label>
                        <input
                          type="text"
                          value={quickAssignData.description || ''}
                          onChange={(e) => setQuickAssignData({...quickAssignData, description: e.target.value})}
                          className={tw.input}
                          placeholder="Description"
                        />
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-xs text-gray-600 font-mono">Press Enter to save & next | Delete to delete document</span>
                      <div className="flex space-x-2">
                        <button
                          onClick={handleQuickSaveExpense}
                          disabled={isSaving}
                          className="flex items-center space-x-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-700 text-white rounded-md font-mono text-sm transition-colors"
                        >
                          <FiSave className="w-4 h-4" />
                          <span>{isSaving ? 'Saving...' : 'Save & Next'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Show assigned property if no quick assign form */}
                {!showQuickAssign && selectedDocument.property_id && (
                  <div className="mt-4 flex items-center justify-between bg-gray-800/80 border border-gray-700/50 rounded-lg p-4">
                    <div className="flex items-center space-x-2">
                      <FiHome className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm text-gray-500 font-mono">Property:</span>
                      <span className="text-sm font-medium text-white font-mono">
                        {getPropertyName(selectedDocument.property_id)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Document Content */}
              <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-6">
                <h3 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-4">Content</h3>

                {/* PDF Viewer */}
                {selectedDocument.filename?.toLowerCase().endsWith('.pdf') ? (
                  <div className="w-full">
                    {loadingPreview ? (
                      <div className="w-full h-[600px] flex items-center justify-center bg-gray-900/50 rounded border border-gray-700/50">
                        <div className={tw.loading}></div>
                      </div>
                    ) : documentUrl ? (
                      <>
                        <object
                          data={documentUrl}
                          type="application/pdf"
                          className="w-full h-[600px] border border-gray-700/50 rounded"
                          title="PDF Document"
                        >
                          <p className="text-gray-500 font-mono p-4">
                            PDF cannot be displayed.{' '}
                            <button
                              onClick={() => window.open(documentUrl, '_blank')}
                              className="text-cyan-400 hover:text-cyan-300 underline font-mono"
                            >
                              Open in new tab
                            </button>
                          </p>
                        </object>
                        <div className="mt-2 flex items-center justify-between">
                          <p className="text-sm text-gray-500 font-mono">PDF Document: {selectedDocument.filename}</p>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => window.open(documentUrl, '_blank')}
                              className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center font-mono transition-colors"
                            >
                              <FiEye className="w-4 h-4 mr-1" />
                              Open in New Tab
                            </button>
                            <button
                              onClick={() => handleDownload(selectedDocument)}
                              className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center font-mono transition-colors"
                            >
                              <FiDownload className="w-4 h-4 mr-1" />
                              Download PDF
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-[600px] flex items-center justify-center bg-gray-900/50 rounded border border-gray-700/50">
                        <p className="text-gray-500 font-mono">Unable to load PDF preview</p>
                      </div>
                    )}
                  </div>
                ) : selectedDocument.filename?.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) ? (
                  // Image viewer
                  <div className="w-full">
                    {loadingPreview ? (
                      <div className="w-full h-96 flex items-center justify-center bg-gray-900/50 rounded border border-gray-700/50">
                        <div className={tw.loading}></div>
                      </div>
                    ) : documentUrl ? (
                      <>
                        <img
                          src={documentUrl}
                          alt={selectedDocument.filename}
                          className="max-w-full h-auto rounded border border-gray-700/50"
                          style={{ maxHeight: '600px' }}
                        />
                        <div className="mt-2 flex items-center justify-between">
                          <p className="text-sm text-gray-500 font-mono">Image: {selectedDocument.filename}</p>
                          <button
                            onClick={() => handleDownload(selectedDocument)}
                            className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center font-mono transition-colors"
                          >
                            <FiDownload className="w-4 h-4 mr-1" />
                            Download Image
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-96 flex items-center justify-center bg-gray-900/50 rounded border border-gray-700/50">
                        <p className="text-gray-500 font-mono">Unable to load image preview</p>
                      </div>
                    )}
                  </div>
                ) : (
                  // Text/HTML content
                  <>
                    {/* View mode toggle for emails with HTML */}
                    {selectedDocument.extracted_data?.html_content && (
                      <div className="mb-4 flex items-center space-x-2 border-b border-gray-700/50 pb-2">
                        <button
                          onClick={() => setViewMode('text')}
                          className={`flex items-center px-3 py-1 rounded text-sm font-mono transition-colors ${
                            viewMode === 'text'
                              ? tw.filterActive
                              : tw.filterInactive
                          }`}
                        >
                          <FiFileText className="w-4 h-4 mr-1" />
                          Text View
                        </button>
                        <button
                          onClick={() => setViewMode('html')}
                          className={`flex items-center px-3 py-1 rounded text-sm font-mono transition-colors ${
                            viewMode === 'html'
                              ? tw.filterActive
                              : tw.filterInactive
                          }`}
                        >
                          <FiCode className="w-4 h-4 mr-1" />
                          HTML View
                        </button>
                      </div>
                    )}

                    {/* Content display */}
                    {viewMode === 'html' && selectedDocument.extracted_data?.html_content ? (
                      <div className="bg-white rounded-lg overflow-hidden">
                        <iframe
                          srcDoc={DOMPurify.sanitize(selectedDocument.extracted_data.html_content)}
                          className="w-full h-96 border-0"
                          title="Email HTML Content"
                          sandbox="allow-same-origin"
                        />
                      </div>
                    ) : (
                      <div className="text-gray-400 whitespace-pre-wrap text-sm font-mono">
                        {selectedDocument.ocr_text ||
                         selectedDocument.extracted_data?.text ||
                         (selectedDocument.from_email ? 'Loading email content...' : 'No content available')}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* AI-Extracted Expense Data */}
              {selectedDocument.extracted_data?.ai_vendor && (
                <div className="mt-6 bg-gray-800/80 border border-cyan-500/20 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xs font-mono text-gray-500 uppercase tracking-wider flex items-center">
                      <FiZap className="w-4 h-4 mr-2 text-cyan-400" />
                      AI-Extracted Expense Information
                    </h3>
                    {selectedDocument.extracted_data.ai_confidence && (
                      <span className={`text-xs px-2 py-1 rounded font-mono border ${
                        selectedDocument.extracted_data.ai_confidence >= 0.8 ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' :
                        selectedDocument.extracted_data.ai_confidence >= 0.6 ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' :
                        'bg-red-500/15 text-red-400 border-red-500/30'
                      }`}>
                        {Math.round(selectedDocument.extracted_data.ai_confidence * 100)}% confidence
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {selectedDocument.extracted_data.ai_vendor && (
                      <div>
                        <span className="text-gray-500 text-xs font-mono uppercase tracking-wider">Vendor</span>
                        <p className="text-white font-medium font-mono">{selectedDocument.extracted_data.ai_vendor}</p>
                      </div>
                    )}

                    {selectedDocument.extracted_data.ai_amount && (
                      <div>
                        <span className="text-gray-500 text-xs font-mono uppercase tracking-wider">Amount</span>
                        <p className="text-cyan-400 font-medium text-lg font-mono">
                          ${selectedDocument.extracted_data.ai_amount.toFixed(2)}
                        </p>
                      </div>
                    )}

                    {selectedDocument.extracted_data.ai_service_date && (
                      <div>
                        <span className="text-gray-500 text-xs font-mono uppercase tracking-wider">Service Date</span>
                        <p className="text-white font-mono">{selectedDocument.extracted_data.ai_service_date}</p>
                      </div>
                    )}

                    {selectedDocument.extracted_data.ai_expense_type && (
                      <div>
                        <span className="text-gray-500 text-xs font-mono uppercase tracking-wider">Expense Type</span>
                        <p className="text-white capitalize font-mono">{selectedDocument.extracted_data.ai_expense_type}</p>
                      </div>
                    )}

                    {selectedDocument.extracted_data.ai_invoice_number && (
                      <div>
                        <span className="text-gray-500 text-xs font-mono uppercase tracking-wider">Invoice #</span>
                        <p className="text-white font-mono">{selectedDocument.extracted_data.ai_invoice_number}</p>
                      </div>
                    )}

                    {selectedDocument.extracted_data.ai_due_date && (
                      <div>
                        <span className="text-gray-500 text-xs font-mono uppercase tracking-wider">Due Date</span>
                        <p className="text-white font-mono">{selectedDocument.extracted_data.ai_due_date}</p>
                      </div>
                    )}
                  </div>

                  {selectedDocument.extracted_data.ai_description && (
                    <div className="mt-4">
                      <span className="text-gray-500 text-xs font-mono uppercase tracking-wider">Description</span>
                      <p className="text-white text-sm mt-1 font-mono">{selectedDocument.extracted_data.ai_description}</p>
                    </div>
                  )}

                  {selectedDocument.extracted_data.ai_notes && (
                    <div className="mt-4">
                      <span className="text-gray-500 text-xs font-mono uppercase tracking-wider">AI Notes</span>
                      <p className="text-gray-400 text-sm mt-1 italic font-mono">{selectedDocument.extracted_data.ai_notes}</p>
                    </div>
                  )}

                </div>
              )}

              {/* Other Extracted Data */}
              {selectedDocument.extracted_data && Object.keys(selectedDocument.extracted_data).length > 0 &&
               !selectedDocument.extracted_data.ai_vendor && (
                <div className="mt-6 bg-gray-800/80 border border-gray-700/50 rounded-lg p-6">
                  <h3 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-4">Extracted Information</h3>
                  <div className="space-y-2">
                    {Object.entries(selectedDocument.extracted_data)
                      .filter(([key]) => key !== 'text' && !key.startsWith('ai_'))
                      .map(([key, value]) => (
                      <div key={key} className="flex">
                        <span className="text-gray-500 w-32 text-sm capitalize font-mono">
                          {key.replace(/_/g, ' ')}:
                        </span>
                        <span className="text-white text-sm font-mono">
                          {JSON.stringify(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className={tw.empty}>
              <FiFile className="w-12 h-12 text-gray-700 mx-auto mb-4" />
              <p>Select a document to preview</p>
            </div>
          )}
        </div>
      </div>

      {/* Subscription Extraction Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className={`${tw.formWrap} max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-mono text-cyan-400 flex items-center">
                <FiCreditCard className="w-5 h-5 mr-2 text-purple-400" />
                Extract Subscription
              </h2>
              <button
                onClick={() => setShowSubscriptionModal(false)}
                className="text-gray-500 hover:text-white transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            {/* Duplicate Warning */}
            {existingSubscription && (
              <div className="mb-6 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 text-amber-400 text-xl font-mono">!</div>
                  <div className="flex-1">
                    <h3 className="text-amber-400 font-mono font-semibold mb-2 text-sm">Existing Subscription Found</h3>
                    <p className="text-gray-400 text-sm font-mono mb-3">
                      A subscription with this name already exists:
                    </p>
                    <div className="bg-gray-900 rounded p-3 space-y-1 text-sm border border-gray-700/50">
                      <p className="text-white font-mono"><strong className="text-gray-500">Name:</strong> {existingSubscription.site_name}</p>
                      <p className="text-white font-mono"><strong className="text-gray-500">Price:</strong> ${existingSubscription.monthly_price}/month</p>
                      <p className="text-white font-mono"><strong className="text-gray-500">Cycle:</strong> {existingSubscription.billing_cycle}</p>
                      <p className="text-white font-mono"><strong className="text-gray-500">Category:</strong> {existingSubscription.category}</p>
                    </div>
                    <p className="text-gray-400 text-sm font-mono mt-3">
                      You can link this document to the existing subscription or create a new one.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className={tw.label}>Service Name *</label>
                <input
                  type="text"
                  value={subscriptionData.site_name}
                  onChange={(e) => setSubscriptionData({...subscriptionData, site_name: e.target.value})}
                  className={tw.input}
                  placeholder="e.g., OpenAI, Shopify"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={tw.label}>Monthly Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={subscriptionData.monthly_price}
                    onChange={(e) => setSubscriptionData({...subscriptionData, monthly_price: parseFloat(e.target.value) || 0})}
                    className={tw.input}
                    placeholder="20.00"
                  />
                </div>

                <div>
                  <label className={tw.label}>Billing Cycle *</label>
                  <select
                    value={subscriptionData.billing_cycle}
                    onChange={(e) => setSubscriptionData({...subscriptionData, billing_cycle: e.target.value})}
                    className={tw.select}
                  >
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Yearly">Yearly</option>
                  </select>
                </div>
              </div>

              <div>
                <label className={tw.label}>Category *</label>
                <select
                  value={subscriptionData.category}
                  onChange={(e) => setSubscriptionData({...subscriptionData, category: e.target.value})}
                  className={tw.select}
                >
                  <option value="">Select category</option>
                  <option value="AI">AI</option>
                  <option value="Business Tools">Business Tools</option>
                  <option value="SaaS">SaaS</option>
                  <option value="Development">Development</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Cloud Services">Cloud Services</option>
                  <option value="TV">TV</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className={tw.label}>URL</label>
                <input
                  type="url"
                  value={subscriptionData.url}
                  onChange={(e) => setSubscriptionData({...subscriptionData, url: e.target.value})}
                  className={tw.input}
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label className={tw.label}>Description</label>
                <textarea
                  value={subscriptionData.description}
                  onChange={(e) => setSubscriptionData({...subscriptionData, description: e.target.value})}
                  className={`${tw.input} resize-none`}
                  rows={3}
                  placeholder="Brief description of the service"
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowSubscriptionModal(false);
                  setExistingSubscription(null);
                }}
                className={tw.btnSecondary}
              >
                Cancel
              </button>
              {existingSubscription ? (
                <>
                  <button
                    onClick={handleLinkToExisting}
                    disabled={isSaving}
                    className="flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-700 text-white rounded-md font-mono text-sm transition-colors"
                  >
                    <FiSave className="w-4 h-4 mr-2" />
                    {isSaving ? 'Linking...' : 'Link to Existing'}
                  </button>
                  <button
                    onClick={handleSaveSubscription}
                    disabled={isSaving || !subscriptionData.site_name || !subscriptionData.category}
                    className="flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 text-white rounded-md font-mono text-sm transition-colors"
                  >
                    <FiSave className="w-4 h-4 mr-2" />
                    {isSaving ? 'Creating...' : 'Create New Anyway'}
                  </button>
                </>
              ) : (
                <button
                  onClick={handleSaveSubscription}
                  disabled={isSaving || !subscriptionData.site_name || !subscriptionData.category}
                  className="flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 text-white rounded-md font-mono text-sm transition-colors"
                >
                  <FiSave className="w-4 h-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save Subscription'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
